<div class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
  <!-- Header Section -->
  <div class="mb-8">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <mat-icon class="text-white">receipt</mat-icon>
          </div>
          Project Bills
        </h1>
        <p class="text-gray-600">View and manage project billing based on project type</p>
      </div>

      <div class="flex gap-3">
        <button
          mat-stroked-button
          (click)="loadProjectBills(); loadSummary()"
          class="!border-gray-300 !text-gray-700 hover:!bg-gray-50"
        >
          <mat-icon class="mr-1">refresh</mat-icon>
          Refresh
        </button>
      </div>
    </div>
  </div>

  <!-- Summary Cards -->
  <div *ngIf="summary" class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
    <mat-card class="shadow-sm">
      <mat-card-content>
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 mb-1">Total Bills</p>
            <p class="text-2xl font-bold text-gray-900">{{ summary.summary.total_bills }}</p>
          </div>
          <mat-icon class="text-blue-500 text-4xl">receipt_long</mat-icon>
        </div>
      </mat-card-content>
    </mat-card>

    <mat-card class="shadow-sm">
      <mat-card-content>
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 mb-1">Total Amount</p>
            <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(summary.summary.total_amount) }}</p>
          </div>
          <mat-icon class="text-green-500 text-4xl">account_balance_wallet</mat-icon>
        </div>
      </mat-card-content>
    </mat-card>

    <mat-card class="shadow-sm">
      <mat-card-content>
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 mb-1">Paid Amount</p>
            <p class="text-2xl font-bold text-green-600">{{ formatCurrency(summary.summary.paid_amount) }}</p>
          </div>
          <mat-icon class="text-green-500 text-4xl">check_circle</mat-icon>
        </div>
      </mat-card-content>
    </mat-card>

    <mat-card class="shadow-sm">
      <mat-card-content>
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 mb-1">Pending Amount</p>
            <p class="text-2xl font-bold text-yellow-600">{{ formatCurrency(summary.summary.pending_amount) }}</p>
          </div>
          <mat-icon class="text-yellow-500 text-4xl">pending</mat-icon>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Filters Section -->
  <mat-card class="mb-6 shadow-sm">
    <mat-card-content>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        
        <mat-form-field appearance="outline" class="w-full">
          
          <input matInput [(ngModel)]="searchKeyword" (keyup.enter)="applyFilter()" placeholder="Search by project name...">
          <mat-icon matPrefix>search</mat-icon>
        </mat-form-field>        
        <mat-form-field appearance="outline" class="w-full">
          
          <mat-select [(ngModel)]="selectedProjectType" (selectionChange)="applyFilter()">
            <mat-option *ngFor="let type of projectTypeOptions" [value]="type.value">
              {{ type.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          
          <mat-select [(ngModel)]="selectedStatus" (selectionChange)="applyFilter()">
            <mat-option *ngFor="let status of statusOptions" [value]="status.value">
              {{ status.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <div class="flex items-center">
          <button
            mat-stroked-button
            (click)="clearFilters()"
            class="w-full !border-gray-300 !text-gray-700"
          >
            <mat-icon class="mr-1">clear</mat-icon>
            Clear Filters
          </button>
        </div>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Loading State -->
  <div *ngIf="loading" class="flex flex-col items-center justify-center py-20">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
    <p class="text-gray-600">Loading project bills...</p>
  </div>

  <!-- Bills Table -->
  <mat-card *ngIf="!loading" class="shadow-sm">
    <mat-card-content>
      <div class="overflow-x-auto">
        <table mat-table [dataSource]="dataSource" matSort class="w-full">
          <!-- Project Name Column -->
          <ng-container matColumnDef="project_name">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Project Name</th>
            <td mat-cell *matCellDef="let bill" class="py-4">
              <div>
                <div class="font-medium text-gray-900">{{ bill.project_name }}</div>
                <div class="text-xs text-gray-500" *ngIf="bill.project_code">Code: {{ bill.project_code }}</div>
                <div class="text-xs text-gray-500" *ngIf="bill.org_name">Org: {{ bill.org_name }}</div>
              </div>
            </td>
          </ng-container>

          <!-- Domain Column -->
          <ng-container matColumnDef="domain">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Domain</th>
            <td mat-cell *matCellDef="let bill">
              <div>
                <div class="text-sm text-gray-900">{{ bill.domain }}</div>
                <div class="text-xs text-gray-500" *ngIf="bill.sub_domain">{{ bill.sub_domain }}</div>
              </div>
            </td>
          </ng-container>

          <!-- Project Type Column -->
          <ng-container matColumnDef="project_type">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Type</th>
            <td mat-cell *matCellDef="let bill">
              <span class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                {{ getProjectTypeLabel(bill.project_type) }}
              </span>
            </td>
          </ng-container>

          <!-- Quantity Column -->
          <ng-container matColumnDef="quantity">
            <th mat-header-cell *matHeaderCellDef>Size/Quantity</th>
            <td mat-cell *matCellDef="let bill">
              <span class="text-sm text-gray-700">{{ formatQuantity(bill.quantity, bill.rate_type) }}</span>
              <div class="text-xs text-gray-500" *ngIf="bill.area > 0 && bill.rate_type !== 'per_project'">
                Area: {{ bill.area | number:'1.2-2' }} sqm
              </div>
            </td>
          </ng-container>

          <!-- Unit Rate Column -->
          <ng-container matColumnDef="unit_rate">
            <th mat-header-cell *matHeaderCellDef>Unit Rate</th>
            <td mat-cell *matCellDef="let bill">
              <span class="text-sm text-gray-700">{{ formatCurrency(bill.unit_rate) }}</span>
              <div class="text-xs text-gray-500">{{ bill.rate_type === 'per_sq_mt' ? 'per sq mt' : bill.rate_type === 'per_psm' ? 'per PSM' : 'per project' }}</div>
            </td>
          </ng-container>

          <!-- Bill Amount Column -->
          <ng-container matColumnDef="bill_amount">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Bill Amount</th>
            <td mat-cell *matCellDef="let bill">
              <div class="flex items-center gap-2">
                <span class="font-semibold text-gray-900">{{ formatCurrency(bill.bill_amount) }}</span>
                <button
                  *ngIf="isSuperAdmin"
                  mat-icon-button
                  matTooltip="Edit Bill Amount (Superadmin Only)"
                  class="text-blue-600 !w-6 !h-6"
                  (click)="editBillAmount(bill)"
                  [disabled]="loading"
                >
                  <mat-icon class="!text-base">edit</mat-icon>
                </button>
              </div>
            </td>
          </ng-container>

          <!-- Status Column -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let bill">
              <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getStatusClass(bill.status)">
                {{ bill.status | titlecase }}
              </span>
            </td>
          </ng-container>

          <!-- Invoice Number Column -->
          <ng-container matColumnDef="invoice_number">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Invoice #</th>
            <td mat-cell *matCellDef="let bill">
              <span class="text-sm text-gray-700" *ngIf="bill.invoice_number">{{ bill.invoice_number }}</span>
              <span class="text-sm text-gray-400 italic" *ngIf="!bill.invoice_number">-</span>
            </td>
          </ng-container>

          <!-- Billing Date Column -->
          <ng-container matColumnDef="billing_date">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Billing Date</th>
            <td mat-cell *matCellDef="let bill">
              <span class="text-sm text-gray-600">{{ bill.billing_date ? (bill.billing_date | date:'short') : '-' }}</span>
            </td>
          </ng-container>

          <!-- Payment Date Column -->
          <ng-container matColumnDef="payment_date">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Payment Date</th>
            <td mat-cell *matCellDef="let bill">
              <span class="text-sm text-gray-600">{{ bill.payment_date ? (bill.payment_date | date:'short') : '-' }}</span>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let bill">
              <div class="flex items-center gap-2">
                <button
                  *ngIf="isSuperAdmin && bill.status === 'pending'"
                  mat-stroked-button
                  color="primary"
                  class="action-btn action-btn-stroked"
                  (click)="updateBillStatus(bill, 'billed')"
                  matTooltip="Mark as Billed"
                >
                  <mat-icon class="action-icon">receipt</mat-icon>
                  <span>Bill</span>
                </button>
                <button
                  *ngIf="bill.status === 'billed' || bill.status === 'pending'"
                  mat-raised-button
                  color="primary"
                  class="action-btn action-btn-raised"
                  (click)="payBill(bill)"
                  [disabled]="loading"
                  matTooltip="Pay Bill"
                >
                  <mat-icon class="action-icon">payment</mat-icon>
                  <span>Pay</span>
                </button>
                <button
                  *ngIf="bill.status === 'paid'"
                  mat-stroked-button
                  class="action-btn action-btn-paid"
                  disabled
                  matTooltip="Bill Paid"
                >
                  <mat-icon class="action-icon">check_circle</mat-icon>
                  <span>Paid</span>
                </button>
                <button
                  mat-icon-button
                  matTooltip="View Bill Details"
                  class="action-icon-btn action-icon-btn-view"
                  (click)="viewBill(bill)"
                >
                  <mat-icon>visibility</mat-icon>
                </button>
                <button
                  mat-icon-button
                  matTooltip="Download Invoice"
                  class="action-icon-btn action-icon-btn-download"
                  (click)="downloadInvoice(bill)"
                  [disabled]="loading"
                >
                  <mat-icon>download</mat-icon>
                </button>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns" class="bg-gray-50"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="hover:bg-gray-50"></tr>
        </table>
      </div>

      <!-- Empty State -->
      <div *ngIf="dataSource.data.length === 0" class="text-center py-12">
        <mat-icon class="text-gray-400 text-6xl mb-4">receipt</mat-icon>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">No Project Bills Found</h3>
        <p class="text-gray-500">Project bills will be automatically created when you create projects</p>
      </div>

      <!-- Paginator -->
      <mat-paginator
        *ngIf="totalItems > 0"
        [length]="totalItems"
        [pageSize]="pageSize"
        [pageIndex]="pageIndex"
        [pageSizeOptions]="pageSizeOptions"
        [showFirstLastButtons]="true"
        (page)="onPageChange($event)"
        class="mt-4"
      ></mat-paginator>
    </mat-card-content>
  </mat-card>
</div>

